import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UserSettingsAction from '@states/user-settings/user-settings.actions';
import { catchError, exhaustMap, Observable, of, share, switchMap, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as SharedActions from '@states/shared/shared.actions';
import { AppState } from '../app.state';
import { UserSettingsService } from '../../development/user-settings.service';
import { UserSettings } from '@models/user-settings';
import { UtilsService } from 'src/app/edge/utils.service';
import { UserSelectors } from '@states/user/user.selector-types';
import { UserActions } from '@states/user/user.action-types';
import { TranslationService } from '../../services/translation.service';
import { TranslateService } from '@ngx-translate/core';
import { UserSettingsActions } from '@states/user-settings/user-settings.action-types';

@Injectable()
export class UserSettingsEffect {
  public saveSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.startUpdateUserSettingsProcess),
      exhaustMap(({ userSettings }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsAction.updateUserSettingsRequest({ userSettings }),
      ]),
    ),
  );

  public sendSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.updateUserSettingsRequest),
      withLatestFrom(this.store$.pipe(select((state) => state.userSettingsState))),
      switchMap(([{ userSettings }, userSettingsState]) => {
        const userSettingsMerged: UserSettings = {
          ...userSettingsState.userSettings,
          ...userSettings,
        };
        return this.sendSetting(userSettingsMerged).pipe(
          switchMap((res) => {
            return [
              SharedActions.setIsSaving({ isSaving: false }),
              UserSettingsAction.setUserSettings({ userSettings: res }),
              SharedActions.showMessage({ success: 'Settings has been updated' }),
            ];
          }),
          catchError((err) => [
            SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
            SharedActions.setIsSaving({ isSaving: false }),
          ]),
        );
      }),
      share(),
    ),
  );

  public getMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.getMe),
      switchMap(({ language }) =>
        this.userSettingsService.me(language).pipe(
          switchMap((res) => {
            if (!res) {
              return [SharedActions.doNothing()];
            }
            return [
              UserSettingsAction.setUserSettings({ userSettings: res }),
              UserSettingsAction.setInterfaceLanguage({ lang: res.lang }),
            ];
          }),
          catchError((err) => [SharedActions.consoleMessage({ error: this.utilsService.errMessage(err) })]),
        ),
      ),
      share(),
    ),
  );

  public verifyPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.verifyPhone),
      withLatestFrom(this.store$.pipe(select(UserSelectors.selectUserState))),
      switchMap(([, { authProviderId, phone }]) =>
        this.userSettingsService.verifyPhone(phone).pipe(
          switchMap((res) => {
            return [
              UserSettingsAction.verifyPhoneSuccess({ id: res['_id'] }),
              SharedActions.showMessage({ success: 'SMS with code sent' }),
            ];
          }),
          catchError((err) => [SharedActions.showMessage({ error: this.utilsService.errMessage(err) })]),
        ),
      ),
      share(),
    ),
  );

  public startConfirmCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.startConfirmCode),
      switchMap(({ code, id }) => [
        SharedActions.setIsSaving({ isSaving: true }),
        UserSettingsAction.confirmCode({ code, id }),
      ]),
      share(),
    ),
  );

  public confirmCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.confirmCode),
      switchMap(({ code, id }) =>
        this.userSettingsService.sendCode({ confirmationCode: code, verificationId: id }).pipe(
          switchMap((res) => {
            return [
              SharedActions.setIsSaving({ isSaving: false }),
              UserActions.setPhoneVerified({ isPhoneVerified: true }),
              UserActions.CreateOrGetUserProfileSuccess({ payload: res }),
              UserSettingsAction.confirmCodeSuccess(),
            ];
          }),
          catchError((err) => [
            SharedActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public validate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.validateUserSettingProperty),
      withLatestFrom(this.store$.pipe(select((state) => state.userSettingsState))),
      switchMap(([{ prop, value }]) => {
        if (this.validateUserSettingProperty(prop, value)) {
          return [
            UserSettingsAction.setValidateStatus({ isValid: true }),
            UserSettingsAction.setUserSettingProperty({ prop, value }),
          ];
        } else {
          return [UserSettingsAction.setValidateStatus({ isValid: false })];
        }
      }),
      share(),
    ),
  );

  public updateUserLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.updateUserLanguage),
      switchMap(({ lang }) => {
        return this.userSettingsService.setLang({ lang }).pipe(
          switchMap((res) => {
            return [
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({
                success: this.translateService.instant('TOASTS.LANGUAGE_UPDATE_SUCCESS'),
              }),
              UserSettingsAction.updateUserLanguageSuccess(),
            ];
          }),
          catchError((err) => [
            SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
            SharedActions.setIsSaving({ isSaving: false }),
            UserSettingsAction.updateUserLanguageFail(),
          ]),
        );
      }),
      share(),
    ),
  );

  public updateUserTheme$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.updateUserTheme),
      switchMap(({ theme }) => {
        return this.userSettingsService.seTheme(theme).pipe(
          switchMap((settings) => {
            return [
              SharedActions.setIsSaving({ isSaving: false }),
              UserSettingsAction.updateUserThemeSuccess({ settings }),
            ];
          }),
          catchError((err) => [
            SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
            SharedActions.setIsSaving({ isSaving: false }),
            UserSettingsAction.updateUserThemeFail(),
          ]),
        );
      }),
      share(),
    ),
  );

  public updateWeeklySubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.updateWeeklySubscription),
      switchMap(({ subForWeekly }) => {
        return this.userSettingsService.setWeeklySubscription(subForWeekly).pipe(
          switchMap((settings) => {
            return [
              SharedActions.setIsSaving({ isSaving: false }),
              UserSettingsAction.updateWeeklySubscriptionSuccess({ settings }),
            ];
          }),
          catchError((err) => [
            SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
            SharedActions.setIsSaving({ isSaving: false }),
            UserSettingsAction.updateWeeklySubscriptionFail(),
          ]),
        );
      }),
      share(),
    ),
  );

  public setInterfaceLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.setInterfaceLanguage),
      withLatestFrom(this.store$.pipe(select((state) => state.organizationState))),
      switchMap(([{ lang }, { activeOrganization }]) => {
        const interfaceLanguage = lang ?? activeOrganization?.lang ?? 'en';
        this.translationService.setLanguage(interfaceLanguage);
        return of(SharedActions.doNothing());
      }),
      share(),
    ),
  );

  public updateDevicePageView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsAction.updateDevicePageView),
      switchMap(({ newDevicePageView }) => {
        return this.userSettingsService.setDevicePageView({ newDevicePageView }).pipe(
          switchMap((res) => {
            return [
              UserSettingsAction.updateDevicePageViewSuccess({ settings: res }),
              SharedActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({
                success: this.translateService.instant('TOASTS.DEVICE_PAGE_VIEW_UPDATE_SUCCESS'),
              }),
            ];
          }),
          catchError((err) => [
            SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
            SharedActions.setIsSaving({ isSaving: false }),
            UserSettingsAction.updateDevicePageViewFail(),
          ]),
        );
      }),
      share(),
    ),
  );

  public getG2Feedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.getG2Feedback),
      switchMap(() =>
        this.userSettingsService.getG2Review().pipe(
          switchMap((res) => {
            return [UserSettingsActions.getG2FeedbackSuccess({ url: res.url })];
          }),
          catchError((err) => {
            return [UserSettingsActions.getG2FeedbackFail()];
          }),
        ),
      ),
      share(),
    ),
  );

  public setG2RemindLater$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.setG2RemindLater),
      switchMap(() =>
        this.userSettingsService.setG2ReviewRemindLater().pipe(
          switchMap((res) => {
            return [UserSettingsActions.setG2RemindLaterSuccess()];
          }),
          catchError((err) => {
            return [UserSettingsActions.setG2RemindLaterFail()];
          }),
        ),
      ),
      share(),
    ),
  );

  public setG2DontAskAgain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.setG2DontAskAgain),
      switchMap(() =>
        this.userSettingsService.setDontAskAgain().pipe(
          switchMap((res) => {
            return [UserSettingsActions.setG2DontAskAgainSuccess()];
          }),
          catchError((err) => {
            return [UserSettingsActions.setG2DontAskAgainFail()];
          }),
        ),
      ),
      share(),
    ),
  );

  public createG2Feedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.createG2Feedback),
      switchMap(({ message, rating }) =>
        this.userSettingsService.g2CreateFeedback({ message, rating }).pipe(
          switchMap((res) => {
            return [
              SharedActions.showMessage({
                success: this.translateService.instant('G2_FEEDBACK_MODAL.TOASTS.CREATE_SUCCESS'),
              }),
              UserSettingsActions.createG2FeedbackSuccess(),
            ];
          }),
          catchError((err) => {
            return [UserSettingsActions.createG2FeedbackFail()];
          }),
        ),
      ),
      share(),
    ),
  );

  public updateUserGlobalSearchSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.updateUserGlobalSearchSettings),
      switchMap(({ settings }) =>
        this.userSettingsService.setGlobalSearchSettings(settings).pipe(
          switchMap((res) => {
            return [
              UserSettingsActions.updateUserGlobalSearchSettingsSuccess({ settings: res }),
              SharedActions.showMessage({
                success: this.translateService.instant('TOASTS.UPDATE_GLOBAL_SEARCH_SETTING_SUCCESS'),
              }),
            ];
          }),
          catchError((err) => {
            const errorMessage = this.utilsService.errMessage(err);
            return [SharedActions.showMessage({ error: errorMessage })];
          }),
        ),
      ),
      share(),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private userSettingsService: UserSettingsService,
    private utilsService: UtilsService,
    private translationService: TranslationService,
    private translateService: TranslateService,
  ) {}

  private sendSetting(userSettings: UserSettings): Observable<any> {
    const _settings = {
      ...userSettings,
      autoLogout: +userSettings.autoLogout,
    };
    if (userSettings._id) {
      return this.userSettingsService.update(_settings);
    } else {
      return this.userSettingsService.create(_settings);
    }
  }

  private isAutoLogoutValid(value): boolean {
    const number = Number(value);
    return !isNaN(number) && number > 0;
  }

  private validateUserSettingProperty(prop: string, value: string) {
    switch (prop) {
      case 'autoLogout':
        return this.isAutoLogoutValid(value);
      default:
        return true;
    }
  }
}
